import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import { Box, Typography, Modal, Checkbox, Switch, Avatar, Backdrop, CircularProgress } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AddIcon from '@mui/icons-material/Add';
import { CommonExcelButton } from '../../../form-components/CommonExcelButton';
import { Close } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import ExcelJS from 'exceljs';
import AnimationPage from '../../../pages/AnimationPage';
import { formatterCurrency } from '../../../utils/methods';
import { getSettlementList, calculateSettlement } from '../../../api/salarySettlement';
import { getSalaryEmployees } from '../../../api/salarySetting';
import CommonModalButton from '../../../form-components/CommonModalButton';
import {
  GuideButton,
  GuideModalContainer,
  GuideModalHeader,
  GuideModalTitle,
  GuideModalBody,
  GuideSection,
  GuideSectionLabel,
  GuideText,
  GuideDivider,
  GuideStepBox,
  GuideStepNumber,
  GuideStepText,
} from '../SalarySetting/SalarySettingStyled';

// ============================================
// Styled - Page
// ============================================
const PageWrapper = styled(Box)({
  padding: '0',
  minHeight: 'calc(100vh - 200px)',
});

const HeaderRow = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: '24px',
});

const MonthNav = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

const NavButton = styled(Box)({
  fontSize: '18px', color: '#BFBFBF', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  '&:hover': { color: '#595959' },
});

const MonthLabel = styled(Typography)({
  fontSize: '500px !important', fontWeight: 700, color: 'red !important', display: 'flex', alignItems: 'center', gap: '10px',
});

const MonthDropdown = styled(Box)({
  display: 'inline-flex', alignItems: 'center', gap: '2px', cursor: 'pointer',
  '&:hover': { color: '#4AB3BC' },
});

const ThisMonthButton = styled(Box)({
  padding: '6px 16px', borderRadius: '6px', background: '#333',
  color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
  '&:hover': { background: '#555' },
});

// ============================================
// Styled - Summary Cards
// ============================================
const SummaryRow = styled(Box)({
  display: 'flex', gap: '16px', marginBottom: '24px',
});

const SummaryCard = styled(Box, {
  shouldForwardProp: prop => prop !== 'topColor',
})(({ topColor }) => ({
  flex: 1, background: '#fff', borderRadius: '12px', padding: '24px',
  border: '1px solid #F0F0F0', borderTop: `4px solid ${topColor || '#4AB3BC'}`,
}));

const SummaryLabel = styled(Typography)({
  fontSize: '14px', fontWeight: 500, color: '#8c8c8c', marginBottom: '8px',
});

const SummaryValue = styled(Typography)({
  fontSize: '24px', fontWeight: 700, color: '#262626',
});

const SummaryBadge = styled(Box)(({ positive }) => ({
  display: 'inline-flex', alignItems: 'center', gap: '4px',
  padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 600, marginTop: '8px',
  background: positive ? '#E6F7EE' : '#FFF1F0',
  color: positive ? '#52C41A' : '#CF1322',
}));

const TriangleUp = ({ color = '#52C41A' }) => (
  <svg width="8" height="6" viewBox="0 0 8 6" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 0L8 6H0L4 0Z" fill={color} />
  </svg>
);

const TriangleDown = ({ color = '#CF1322' }) => (
  <svg width="8" height="6" viewBox="0 0 8 6" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 6L0 0H8L4 6Z" fill={color} />
  </svg>
);

const PctBadge = ({ value, label, invertColor = false }) => {
  if (value === null || value === undefined) return null;
  const num = Number(value);
  const isPositive = invertColor ? num <= 0 : num >= 0;
  const badgeColor = isPositive ? '#52C41A' : '#CF1322';
  return (
    <SummaryBadge positive={isPositive}>
      {num >= 0 ? <TriangleUp color={badgeColor} /> : <TriangleDown color={badgeColor} />}
      {num >= 0 ? `${value}%` : `${Math.abs(num).toFixed(1)}%`} {label}
    </SummaryBadge>
  );
};

// ============================================
// Styled - Charts
// ============================================
const ChartRow = styled(Box)({
  display: 'flex', gap: '16px', marginBottom: '24px',
});

const ChartCard = styled(Box)({
  background: '#fff', borderRadius: '12px', padding: '24px',
  border: '1px solid #F0F0F0',
});

const ChartTitle = styled(Typography)({
  fontSize: '16px', fontWeight: 700, color: '#262626', marginBottom: '16px',
  display: 'flex', alignItems: 'center', gap: '8px',
});

// 섹션 아이콘 (피그마 에셋)
const BarChartIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none"><rect width="32" height="32" rx="4" fill="#333"/><path d="M6 10.66c0-1.07.87-1.94 1.94-1.94 1.07 0 1.93.87 1.93 1.94V26H6V10.66z" fill="#4AB3BC"/><path d="M10.52 18.41c0-1.07.87-1.94 1.93-1.94 1.07 0 1.94.87 1.94 1.94V26h-3.87v-7.59z" fill="#FFB659"/><path d="M17.61 7.94c0-1.07.87-1.94 1.94-1.94 1.07 0 1.93.87 1.93 1.94V26h-3.87V7.94z" fill="#4AB3BC"/><path d="M22.13 16.91c0-1.07.87-1.94 1.93-1.94 1.07 0 1.94.87 1.94 1.94V26h-3.87V16.91z" fill="#FFB659"/></svg>
);

const DonutChartIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 0c1.53 0 3.05.35 4.42 1.03 1.38.68 2.58 1.67 3.51 2.88.93 1.22 1.58 2.63 1.87 4.14.3 1.5.25 3.06-.15 4.54-.4 1.48-1.13 2.85-2.14 4-.5.58-1.77 1.48-3.19 2.07-1.42.59-2.95.84-4.48.74-1.53-.1-3.02-.55-4.35-1.32l1.4-2.42c.96.55 2.03.88 3.13.95 1.1.07 2.2-.11 3.23-.54 1.02-.42 1.93-1.07 2.66-1.9.73-.83 1.26-1.82 1.54-2.88.29-1.07.32-2.19.11-3.27a7.2 7.2 0 00-1.35-3.08 7.2 7.2 0 00-2.53-2.17A7.2 7.2 0 0010 2.8V0z" fill="#55C982"/><path d="M.34 7.41A10 10 0 0110 0v2.8a7.2 7.2 0 00-6.95 5.34L.34 7.41z" fill="#3582C0"/><path d="M5 18.66A10 10 0 01.34 7.41l2.7.73a7.2 7.2 0 003.36 8.1L5 18.66z" fill="#F59E0B"/></svg>
);

const TrophyIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none"><rect width="32" height="32" rx="4" fill="#333"/><path d="M10.44 14.67V10.44H8.22v1.11c0 .7.2 1.34.61 1.9.41.57.95.97 1.61 1.21zm11.11 0c.67-.24 1.2-.65 1.61-1.21.41-.57.61-1.2.61-1.9v-1.11h-2.22v4.22zm-6.67 9.11v-3.44c-.91-.2-1.72-.59-2.43-1.15a5.36 5.36 0 01-1.57-2.13c-1.39-.17-2.55-.77-3.49-1.82C8.47 14.19 8 12.96 8 11.56v-1.11c0-.61.22-1.13.65-1.57.44-.43.96-.65 1.57-.65h2.22c0-.61.22-1.13.65-1.57.44-.43.96-.65 1.57-.65h6.67c.61 0 1.13.22 1.57.65.44.44.66.96.66 1.57h2.22c.61 0 1.13.22 1.57.65.44.44.66.96.66 1.57v1.11c0 1.41-.47 2.63-1.4 3.68-.94 1.05-2.1 1.65-3.5 1.82-.33.85-.86 1.56-1.57 2.13-.71.57-1.52.95-2.43 1.15v3.44h3.33c.31 0 .58.11.79.32.21.21.32.49.32.79 0 .31-.11.58-.32.79-.22.21-.49.32-.79.32h-8.89c-.31 0-.58-.11-.79-.32-.22-.21-.32-.49-.32-.79 0-.31.11-.58.32-.79.21-.21.49-.32.79-.32h3.33z" fill="#FFB659"/></svg>
);

const LessonChartIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none"><rect width="32" height="32" rx="4" fill="#333"/><path d="M20.68 19.03l3.78-3.58.65.06c.28.04.5.17.66.39.16.22.24.46.24.72 0 .15-.03.29-.08.44-.05.15-.14.27-.27.39l-2.59 2.5.77 3.72c.02.04.03.08.03.12v.12c0 .31-.1.57-.3.79-.2.21-.43.32-.7.32-.08 0-.17-.01-.26-.04-.09-.03-.18-.07-.26-.12l-.52-.36-1.15-5.45zm-2.34-8.04l-1.04-2.7.22-.6c.08-.22.21-.39.39-.51.17-.12.35-.18.54-.18.18 0 .36.06.54.17.17.11.3.27.38.5l1.37 3.58-2.39-.25zM8.89 23.65l1.09-5.2L6.32 14.95c-.13-.11-.22-.24-.26-.39-.04-.15-.06-.29-.06-.44 0-.26.08-.5.24-.72.16-.22.37-.35.66-.39l4.83-.47 1.87-4.9c.08-.22.21-.39.39-.5.18-.11.36-.17.53-.17.18 0 .36.06.54.17.17.11.3.28.38.5l1.87 4.9 4.83.47c.28.04.5.17.66.39.16.22.24.46.23.72 0 .15-.02.29-.06.44-.04.15-.13.27-.26.39l-3.66 3.5 1.09 5.2c.02.06.03.14.03.25 0 .31-.1.57-.3.79-.2.21-.43.32-.7.32-.05 0-.22-.06-.52-.17l-4.13-2.75-4.13 2.75c-.08.06-.17.1-.26.12-.09.03-.18.04-.26.04-.3 0-.56-.13-.77-.4-.22-.27-.28-.58-.2-.96z" fill="#FFB659"/></svg>
);

const ChartDot = styled(Box)(({ color }) => ({
  width: '10px', height: '10px', borderRadius: '2px', background: color,
}));

const MEDAL_ICONS = ['🥇', '🥈', '🥉'];

// ============================================
// Styled - TOP5 & 수업 현황
// ============================================
const BottomRow = styled(Box)({
  display: 'flex', gap: '16px', marginBottom: '24px',
});

const BottomCard = styled(Box)({
  flex: 1, background: '#fff', borderRadius: '12px', padding: '24px',
  border: '1px solid #F0F0F0',
});

const MiniTable = styled(Box)({ width: '100%' });

const MiniTableHead = styled(Box)({
  display: 'flex', borderBottom: '1px solid #E8E8E8', padding: '8px 0',
});

const MiniTableHeadCell = styled(Box)(({ flex }) => ({
  flex: flex || 1, fontSize: '12px', fontWeight: 600, color: '#8c8c8c', textAlign: 'center',
}));

const MiniTableRow = styled(Box)({
  display: 'flex', alignItems: 'center', padding: '10px 0',
  borderBottom: '1px solid #F5F5F5',
});

const MiniTableCell = styled(Box)(({ flex, highlight }) => ({
  flex: flex || 1, fontSize: '13px', color: highlight ? '#4AB3BC' : '#404040',
  fontWeight: highlight ? 700 : 400, textAlign: 'center',
}));

// ============================================
// Styled - Table
// ============================================
const TableCard = styled(Box)({
  border: '1px solid #F0F0F0', borderRadius: '12px', overflow: 'hidden', background: '#fff',
});

const FilterRow = styled(Box)({
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  borderBottom: '1px solid #E8E8E8',
});

const FilterLeft = styled(Box)({ display: 'flex', alignItems: 'center' });

const FilterRight = styled(Box)({
  display: 'flex', alignItems: 'center', gap: '8px',
});

const FilterTab = styled(Box)(({ active }) => ({
  padding: '12px 20px', fontSize: '13px', fontWeight: active ? 700 : 500,
  cursor: 'pointer', color: active ? '#4AB3BC' : '#595959',
  borderBottom: active ? '2px solid #4AB3BC' : '2px solid transparent',
  '&:hover': { color: '#4AB3BC' },
}));

const FilterSelect = styled('select')({
  height: '36px', padding: '0 28px 0 12px', border: '1px solid #D9D9D9', borderRadius: '8px',
  fontSize: '13px', color: '#404040', background: '#fff', outline: 'none',
  cursor: 'pointer', fontFamily: 'Pretendard, sans-serif',
  appearance: 'none', WebkitAppearance: 'none',
  backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'6\' viewBox=\'0 0 10 6\'%3E%3Cpath d=\'M5 6L0 0h10L5 6z\' fill=\'%23999\'/%3E%3C/svg%3E")',
  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center',
  '&:hover': { borderColor: '#4AB3BC' },
});

const CreateButton = styled(Box)({
  display: 'flex', alignItems: 'center', gap: '4px',
  padding: '8px 16px', borderRadius: '8px', background: '#333',
  color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
  marginRight: '16px', '&:hover': { background: '#555' },
});

const Table = styled(Box)({ minHeight: '520px', maxHeight: '520px', overflowY: 'auto' });

const TableHead = styled(Box)({
  display: 'flex', background: '#FAFAFA', borderBottom: '1px solid #E8E8E8',
  position: 'sticky', top: 0, zIndex: 1,
});

const TableHeadCell = styled(Box)(({ flex }) => ({
  flex: flex || 1, padding: '12px 8px', fontSize: '12px',
  fontWeight: 600, color: '#595959', textAlign: 'center',
  borderRight: '1px solid #E8E8E8',
  '&:last-child': { borderRight: 'none' },
}));

const TableRow = styled(Box)(({ expanded }) => ({
  display: 'flex', borderBottom: '1px solid #F0F0F0', cursor: 'pointer',
  background: expanded ? '#F0FAF9' : '#fff',
  '&:hover': { background: '#F7FFFE' },
}));

const TableCell = styled(Box)(({ flex, highlight }) => ({
  flex: flex || 1, padding: '12px 8px', fontSize: '12px',
  color: highlight ? '#4AB3BC' : '#404040', fontWeight: highlight ? 600 : 400,
  textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
  borderRight: '1px solid #F0F0F0',
  '&:last-child': { borderRight: 'none' },
}));

const StatusBadge = styled(Box)(({ status }) => ({
  display: 'inline-flex', padding: '2px 10px', borderRadius: '4px',
  fontSize: '12px', fontWeight: 600,
  ...(status === 'DRAFT' && { background: '#FFF7E6', color: '#FA8C16' }),
  ...(status === 'CONFIRMED' && { background: '#F0F0F0', color: '#595959' }),
}));

const SalaryTypeBadge = styled(Box)({
  display: 'inline-flex', padding: '2px 10px', borderRadius: '4px',
  fontSize: '12px', fontWeight: 600, background: '#E6F7F8', color: '#4AB3BC',
});

const EmptyMessage = styled(Box)({
  padding: '60px 0', textAlign: 'center', color: '#999', fontSize: '14px',
});

// ============================================
// Styled - Modal
// ============================================
const ModalContainer = styled(Box)({
  position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
  width: '520px', maxHeight: '70vh', backgroundColor: '#fff', borderRadius: '16px',
  overflow: 'hidden', outline: 'none', display: 'flex', flexDirection: 'column',
});

const ModalHeader = styled(Box)({
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '16px 24px', backgroundColor: '#333', color: '#fff',
});

const ModalTitle = styled(Typography)({ fontSize: '16px', fontWeight: 700, color: '#fff' });

const ModalBody = styled(Box)({ padding: '20px 24px', overflowY: 'auto', flex: 1 });

const ModalFooter = styled(Box)({
  display: 'flex', justifyContent: 'center', gap: '12px',
  padding: '16px 24px', borderTop: '1px solid #f0f0f0',
});

const ConfirmModalContainer = styled(Box)({
  position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
  width: '400px', backgroundColor: '#fff', borderRadius: '16px', padding: '32px',
  textAlign: 'center', outline: 'none',
});

const ConfirmModalText = styled(Typography)({
  fontSize: '22px', fontWeight: 600, color: '#333', marginBottom: '24px',
});

const ConfirmModalButtons = styled(Box)({ display: 'flex', justifyContent: 'center', gap: '8px' });

const EmployeeRow = styled(Box)({
  display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 8px',
  borderRadius: '8px', cursor: 'pointer', '&:hover': { background: '#F0FAF9' },
});

const EmployeeAvatar = styled(Avatar)({
  width: '36px', height: '36px',
});

const EmployeeInfo = styled(Box)({ flex: 1 });
const EmployeeName = styled(Typography)({ fontSize: '14px', fontWeight: 600, color: '#262626' });
const EmployeeSub = styled(Typography)({ fontSize: '12px', color: '#8c8c8c' });
const PositionGroupTitle = styled(Typography)({
  fontSize: '13px', fontWeight: 600, color: '#8c8c8c',
  padding: '12px 8px 4px', borderBottom: '1px solid #f0f0f0', marginBottom: '4px',
});
const TealCheckbox = styled(Checkbox)({
  padding: '4px', color: '#D9D9D9', '&.Mui-checked': { color: '#4AB3BC' },
});

// ============================================
// SVG Charts
// ============================================
const CHART_COLORS = { pay: '#4AB3BC', deduction: '#FFB547' };
const DONUT_COLORS = ['#4AB3BC', '#FFB547', '#55C982'];
const LESSON_COLORS = { group: '#4AB3BC', private: '#3582C0' };

const ChartTooltip = styled(Box)({
  position: 'absolute', pointerEvents: 'none',
  background: '#333', color: '#fff', borderRadius: '8px', padding: '8px 12px',
  fontSize: '12px', fontWeight: 500, whiteSpace: 'nowrap', zIndex: 10,
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  '&::after': {
    content: '""', position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
    border: '5px solid transparent', borderTopColor: '#333',
  },
});

const BarChart = ({ data, height = 200 }) => {
  const [hovered, setHovered] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const handleMouseMove = useCallback((e, i) => {
    const rect = e.currentTarget.closest('svg').getBoundingClientRect();
    setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top - 10 });
    setHovered(i);
  }, []);

  if (!data || data.length === 0) return null;
  const maxVal = Math.max(...data.map(d => Math.max(d.pay || 0, d.deduction || 0)), 1);
  const barW = Math.floor(600 / data.length / 3);
  const gap = Math.floor(600 / data.length);

  return (
    <Box sx={{ position: 'relative' }}>
      <svg width="100%" viewBox={`0 0 ${gap * data.length + 40} ${height + 40}`} style={{ overflow: 'visible' }}>
        {[0, 0.25, 0.5, 0.75, 1].map((r, i) => {
          const y = height - height * r + 20;
          return (
            <g key={i}>
              <line x1="40" y1={y} x2={gap * data.length + 40} y2={y} stroke="#F0F0F0" strokeWidth="1" />
              <text x="36" y={y + 4} textAnchor="end" fontSize="10" fill="#8c8c8c">
                {Math.round(maxVal * r / 10000)}만
              </text>
            </g>
          );
        })}
        {data.map((d, i) => {
          const x = i * gap + 40;
          const payH = (d.pay / maxVal) * height;
          const dedH = (d.deduction / maxVal) * height;
          const isHovered = hovered === i;
          return (
            <g key={i} onMouseMove={e => handleMouseMove(e, i)} onMouseLeave={() => setHovered(null)} style={{ cursor: 'pointer' }}>
              <rect x={x} y="0" width={gap} height={height + 40} fill="transparent" />
              <rect x={x + gap / 2 - barW - 2} y={height - payH + 20} width={barW} height={payH} rx="3"
                fill={CHART_COLORS.pay} opacity={isHovered ? 1 : 0.85} />
              <rect x={x + gap / 2 + 2} y={height - dedH + 20} width={barW} height={dedH} rx="3"
                fill={CHART_COLORS.deduction} opacity={isHovered ? 1 : 0.85} />
              <text x={x + gap / 2} y={height + 36} textAnchor="middle" fontSize="11"
                fill={isHovered ? '#262626' : '#8c8c8c'} fontWeight={isHovered ? 600 : 400}>
                {d.label}
              </text>
            </g>
          );
        })}
      </svg>
      {hovered !== null && data[hovered] && (
        <ChartTooltip sx={{ left: `${tooltipPos.x}px`, top: `${tooltipPos.y - 50}px`, transform: 'translateX(-50%)' }}>
          <Box sx={{ fontWeight: 700, mb: '4px' }}>{data[hovered].label}</Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Box sx={{ width: 8, height: 8, borderRadius: '2px', background: '#4AB3BC', flexShrink: 0 }} />실지급액 : {formatterCurrency(data[hovered].pay)}원</Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Box sx={{ width: 8, height: 8, borderRadius: '2px', background: '#FFB547', flexShrink: 0 }} />공제액 : {formatterCurrency(data[hovered].deduction)}원</Box>
        </ChartTooltip>
      )}
    </Box>
  );
};

const DonutChart = ({ data, size = 160 }) => {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const validData = data.filter(d => d.value > 0);
  const total = validData.reduce((sum, d) => sum + d.value, 0);
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 10;
  const innerR = r * 0.6;

  const handleMouseMove = useCallback((e, i) => {
    const rect = e.currentTarget.closest('[data-donut]').getBoundingClientRect();
    setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top - 10 });
    setHoveredIdx(i);
  }, []);

  // 데이터 1개면 원 2개(바깥+안쪽)로 그림
  if (validData.length === 1) {
    const d = validData[0];
    const color = DONUT_COLORS[data.indexOf(d) % DONUT_COLORS.length];
    return (
      <Box sx={{ position: 'relative' }} data-donut>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={cx} cy={cy} r={r} fill={color} style={{ cursor: 'pointer' }}
            onMouseMove={e => handleMouseMove(e, 0)} onMouseLeave={() => setHoveredIdx(null)} />
          <circle cx={cx} cy={cy} r={innerR} fill="#fff" />
        </svg>
        {hoveredIdx === 0 && (
          <ChartTooltip sx={{ left: `${tooltipPos.x}px`, top: `${tooltipPos.y - 50}px`, transform: 'translateX(-50%)' }}>
            <Box sx={{ fontWeight: 700, mb: '2px' }}>{d.label}</Box>
            <Box>{d.value}명 (100%)</Box>
          </ChartTooltip>
        )}
      </Box>
    );
  }

  // 데이터 없으면 빈 도넛
  if (total === 0) {
    return (
      <Box sx={{ position: 'relative' }} data-donut>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={cx} cy={cy} r={r} fill="#F0F0F0" />
          <circle cx={cx} cy={cy} r={innerR} fill="#fff" />
        </svg>
      </Box>
    );
  }

  let startAngle = -90;
  const segments = validData.map((d, i) => {
    const angle = (d.value / total) * 360;
    const endAngle = startAngle + angle;
    const largeArc = angle > 180 ? 1 : 0;
    const toRad = a => (a * Math.PI) / 180;
    const x1 = cx + r * Math.cos(toRad(startAngle));
    const y1 = cy + r * Math.sin(toRad(startAngle));
    const x2 = cx + r * Math.cos(toRad(endAngle));
    const y2 = cy + r * Math.sin(toRad(endAngle));
    const ix1 = cx + innerR * Math.cos(toRad(endAngle));
    const iy1 = cy + innerR * Math.sin(toRad(endAngle));
    const ix2 = cx + innerR * Math.cos(toRad(startAngle));
    const iy2 = cy + innerR * Math.sin(toRad(startAngle));
    const pathD = `M${x1},${y1} A${r},${r} 0 ${largeArc},1 ${x2},${y2} L${ix1},${iy1} A${innerR},${innerR} 0 ${largeArc},0 ${ix2},${iy2} Z`;
    startAngle = endAngle;
    const origIdx = data.indexOf(d);
    return { pathD, color: DONUT_COLORS[origIdx % DONUT_COLORS.length], label: d.label, value: d.value, pct: (d.value / total * 100).toFixed(1) };
  });

  return (
    <Box sx={{ position: 'relative' }} data-donut>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {segments.map((seg, i) => (
          <path key={i} d={seg.pathD} fill={seg.color}
            opacity={hoveredIdx === null || hoveredIdx === i ? 1 : 0.4}
            style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
            onMouseMove={e => handleMouseMove(e, i)}
            onMouseLeave={() => setHoveredIdx(null)} />
        ))}
      </svg>
      {hoveredIdx !== null && segments[hoveredIdx] && (
        <ChartTooltip sx={{ left: `${tooltipPos.x}px`, top: `${tooltipPos.y - 50}px`, transform: 'translateX(-50%)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 700, mb: '2px' }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '2px', background: segments[hoveredIdx].color, flexShrink: 0 }} />
            {segments[hoveredIdx].label}
          </Box>
          <Box>{segments[hoveredIdx].value}명 ({segments[hoveredIdx].pct}%)</Box>
        </ChartTooltip>
      )}
    </Box>
  );
};

// ============================================
// Constants
// ============================================
const SALARY_TYPE_LABELS = { COUNT: '횟수제', MONTHLY: '월급제', HOURLY: '시간제' };
const STATUS_LABELS = { DRAFT: '미정산', CONFIRMED: '정산완료' };
const TYPE_FILTERS = [
  { key: 'ALL', label: '전체 내역' },
  { key: 'COUNT', label: '횟수제 강사' },
  { key: 'HOURLY', label: '시간제 직원' },
  { key: 'MONTHLY', label: '월급제 직원' },
];

// ============================================
// Component
// ============================================
const SalarySettlementList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const user = useSelector(state => state.user.user);
  const branchId = user?.branch?.branchId;

  const [currentDate, setCurrentDate] = useState(() => {
    const y = searchParams.get('year');
    const m = searchParams.get('month');
    if (y && m) return dayjs(`${y}-${String(m).padStart(2, '0')}-01`);
    return dayjs();
  });

  // 상세에서 돌아올 때 searchParams 변경 감지
  useEffect(() => {
    const y = searchParams.get('year');
    const m = searchParams.get('month');
    if (y && m) {
      setCurrentDate(dayjs(`${y}-${String(m).padStart(2, '0')}-01`));
    }
  }, [searchParams]);

  const year = currentDate.year();
  const month = currentDate.month() + 1;

  // 월 이동(화살표/드롭다운/이번달)은 currentDate만 바꾸고 URL은 그대로라, 상세로 들어갔다 뒤로가기/
  // 목록으로를 누르면 월 선택이 날아가는 문제가 있었다. URL을 항상 currentDate와 맞춰 둔다.
  // searchParams가 이미 같은 값이면 navigate를 또 호출하지 않아야 무한 루프를 막는다.
  useEffect(() => {
    const curY = searchParams.get('year');
    const curM = searchParams.get('month');
    if (String(year) !== curY || String(month) !== curM) {
      navigate(`/salarySettlement?year=${year}&month=${month}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [createConfirmModalOpen, setCreateConfirmModalOpen] = useState(false);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [yearDropOpen, setYearDropOpen] = useState(false);
  const [monthDropOpen, setMonthDropOpen] = useState(false);
  const yearRef = useRef(null);
  const monthRef = useRef(null);

  // 드롭다운 바깥 클릭 시 닫기
  useEffect(() => {
    const handleClick = e => {
      if (yearRef.current && !yearRef.current.contains(e.target)) setYearDropOpen(false);
      if (monthRef.current && !monthRef.current.contains(e.target)) setMonthDropOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const [typeFilter, setTypeFilter] = useState('ALL');
  const [positionFilter, setPositionFilter] = useState('ALL');
  const [onlyDraft, setOnlyDraft] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { data: settlements = [] } = useQuery({
    queryKey: ['settlementList', branchId, year, month],
    queryFn: () => getSettlementList(branchId, year, month, user.token),
    enabled: !!branchId,
    select: data => data || [],
  });

  const { data: employeeGroups = [] } = useQuery({
    queryKey: ['salaryEmployees', branchId],
    queryFn: () => getSalaryEmployees(branchId, true, user.token),
    enabled: !!branchId && createModalOpen,
    select: data => data || [],
  });

  // 과거 6개월 데이터 (차트 + 전월 대비용)
  const pastMonths = useMemo(() => {
    return Array.from({ length: 6 }, (_, i) => {
      const d = currentDate.subtract(i + 1, 'month');
      return { year: d.year(), month: d.month() + 1, key: `${d.year()}-${d.month() + 1}` };
    });
  }, [currentDate]);

  const pastQueries = useQueries({
    queries: pastMonths.map(pm => ({
      queryKey: ['settlementList', branchId, pm.year, pm.month],
      queryFn: () => getSettlementList(branchId, pm.year, pm.month, user.token),
      enabled: !!branchId,
      select: data => data || [],
      staleTime: 5 * 60 * 1000,
    })),
  });

  const pastDataMap = useMemo(() => {
    const map = {};
    pastMonths.forEach((pm, i) => {
      map[pm.key] = pastQueries[i]?.data || [];
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pastMonths, ...pastQueries.map(q => q.data)]);

  const prevMonth = currentDate.subtract(1, 'month');
  const prevSettlements = pastDataMap[`${prevMonth.year()}-${prevMonth.month() + 1}`] || [];

  // 직급 목록: 직원 목록 API에서 추출 (정산 데이터에 직급이 없을 수 있으므로)
  const { data: positionGroups = [] } = useQuery({
    queryKey: ['salaryEmployeesForPositions', branchId],
    queryFn: () => getSalaryEmployees(branchId, false, user.token),
    enabled: !!branchId,
  });
  const positions = useMemo(() => {
    const set = new Set(positionGroups.map(g => g.positionName).filter(Boolean));
    return [...set];
  }, [positionGroups]);

  const filteredSettlements = useMemo(() => {
    let filtered = settlements;
    if (typeFilter !== 'ALL') filtered = filtered.filter(s => s.salaryType === typeFilter);
    if (positionFilter !== 'ALL') filtered = filtered.filter(s => s.employeePosition === positionFilter);
    if (onlyDraft) filtered = filtered.filter(s => s.status === 'DRAFT');
    return filtered;
  }, [settlements, typeFilter, positionFilter, onlyDraft]);

  const totalPages = Math.ceil(filteredSettlements.length / pageSize);
  const pagedSettlements = filteredSettlements.slice((page - 1) * pageSize, page * pageSize);

  // 전월 데이터를 employeeId 기준으로 맵핑
  const prevByEmployee = useMemo(() => {
    const map = {};
    prevSettlements.forEach(s => { map[s.employeeId] = s; });
    return map;
  }, [prevSettlements]);

  const settledEmployeeIds = new Set(settlements.map(s => s.employeeId));

  // ============================================
  // 요약 데이터 계산
  // ============================================
  const stats = useMemo(() => {
    const confirmed = settlements.filter(s => s.status === 'CONFIRMED').length;
    const draft = settlements.length - confirmed;
    const totalLesson = settlements.reduce((sum, s) => sum + (s.totalLessonCount || 0), 0);
    const totalPay = settlements.reduce((sum, s) => sum + (s.totalPayAmount || 0), 0);
    const totalDeduction = settlements.reduce((sum, s) => sum + (s.deductionAmount || 0), 0);
    const totalNet = settlements.reduce((sum, s) => sum + (s.netPayAmount || 0), 0);
    const prevTotalPay = prevSettlements.reduce((sum, s) => sum + (s.totalPayAmount || 0), 0);
    const prevTotalDeduction = prevSettlements.reduce((sum, s) => sum + (s.deductionAmount || 0), 0);
    const prevTotalNet = prevSettlements.reduce((sum, s) => sum + (s.netPayAmount || 0), 0);
    const prevTotalLesson = prevSettlements.reduce((sum, s) => sum + (s.totalLessonCount || 0), 0);
    const pct = (cur, prev) => prev > 0 ? ((cur - prev) / prev * 100).toFixed(1) : null;
    return {
      confirmed, draft, totalLesson, totalPay, totalDeduction, totalNet,
      lessonPct: pct(totalLesson, prevTotalLesson),
      payPct: pct(totalPay, prevTotalPay),
      deductionPct: pct(totalDeduction, prevTotalDeduction),
      netPct: pct(totalNet, prevTotalNet),
      confirmPct: settlements.length > 0 ? (confirmed / settlements.length * 100).toFixed(1) : 0,
      draftPct: settlements.length > 0 ? (draft / settlements.length * 100).toFixed(1) : 0,
    };
  }, [settlements, prevSettlements]);

  // 유형별 분포
  const typeDistribution = useMemo(() => {
    const groups = {};
    settlements.forEach(s => {
      const type = s.salaryType || 'COUNT';
      if (!groups[type]) groups[type] = { count: 0, total: 0 };
      groups[type].count++;
      groups[type].total += s.netPayAmount || 0;
    });
    return ['COUNT', 'HOURLY', 'MONTHLY']
      .filter(k => groups[k])
      .map(k => ({ key: k, label: SALARY_TYPE_LABELS[k], ...groups[k] }));
  }, [settlements]);

  // TOP 5
  const top5 = useMemo(() => {
    return [...settlements]
      .sort((a, b) => (b.netPayAmount || 0) - (a.netPayAmount || 0))
      .slice(0, 5);
  }, [settlements]);

  // 횟수제 강사 수업 현황
  const countInstructors = useMemo(() => {
    return settlements
      .filter(s => s.salaryType === 'COUNT')
      .sort((a, b) => (b.totalLessonCount || 0) - (a.totalLessonCount || 0))
      .slice(0, 8);
  }, [settlements]);
  const maxLessonCount = Math.max(...countInstructors.map(s => s.totalLessonCount || 0), 1);

  // 바 차트 데이터 (최근 7개월, 전부 실제 데이터)
  const barChartData = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = currentDate.subtract(6 - i, 'month');
      const y = d.year();
      const m = d.month() + 1;
      const key = `${y}-${m}`;
      const data = (y === year && m === month) ? settlements : (pastDataMap[key] || []);
      return {
        label: `${m}월`,
        pay: data.reduce((sum, s) => sum + (s.netPayAmount || 0), 0),
        deduction: data.reduce((sum, s) => sum + (s.deductionAmount || 0), 0),
      };
    });
  }, [settlements, pastDataMap, currentDate, year, month]);

  // ============================================
  // Mutations & Handlers
  // ============================================
  const calcMutation = useMutation({
    mutationFn: employeeId => calculateSettlement({ employeeId, branchId, year, month }, user.token),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settlementList'] }),
  });

  const handleExcelDownload = async () => {
    if (filteredSettlements.length === 0) { toast.error('다운로드할 데이터가 없습니다'); return; }
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet(`급여정산_${year}년${month}월`);
    ws.columns = [
      { header: '정산상태', key: 'status', width: 12 },
      { header: '임직원', key: 'name', width: 12 },
      { header: '급여유형', key: 'type', width: 12 },
      { header: '수업일수', key: 'workDays', width: 10 },
      { header: '정산 수업', key: 'lessonCount', width: 12 },
      { header: '기본급', key: 'base', width: 15 },
      { header: '수업수당', key: 'lesson', width: 15 },
      { header: '초과수당', key: 'overflow', width: 15 },
      { header: '성과급', key: 'bonus', width: 15 },
      { header: '비과세', key: 'taxExempt', width: 15 },
      { header: '근로수당', key: 'work', width: 15 },
      { header: '급여총액', key: 'totalPay', width: 18 },
      { header: '공제액', key: 'deduction', width: 15 },
      { header: '실지급액', key: 'netPay', width: 18 },
    ];
    ws.getRow(1).font = { bold: true };
    ws.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } };
    filteredSettlements.forEach(s => {
      ws.addRow({
        status: STATUS_LABELS[s.status] || s.status,
        name: s.employeeName,
        type: SALARY_TYPE_LABELS[s.salaryType] || s.salaryType,
        workDays: s.workDays || 0,
        lessonCount: s.totalLessonCount || 0,
        base: s.baseAmount || 0,
        lesson: (s.privateLessonAmount || 0) + (s.groupLessonAmount || 0),
        overflow: s.overflowAmount || 0,
        bonus: s.bonusAmount || 0,
        taxExempt: s.taxExemptAmount || 0,
        work: (s.overtimeAmount || 0) + (s.nightWorkAmount || 0) + (s.holidayWorkAmount || 0) + (s.weeklyHolidayAmount || 0),
        totalPay: s.totalPayAmount || 0,
        deduction: s.deductionAmount || 0,
        netPay: s.netPayAmount || 0,
      });
    });
    // 금액 컬럼 포맷
    ['base','lesson','overflow','bonus','taxExempt','work','totalPay','deduction','netPay'].forEach(key => {
      const col = ws.getColumn(key);
      col.numFmt = '#,##0';
    });
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `급여정산_${year}년${month}월_${dayjs().format('YYYYMMDD')}.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
    toast.success('엑셀 다운로드가 완료되었습니다');
  };

  const handleCreateSettlements = async () => {
    if (selectedEmployeeIds.length === 0) { toast.error('직원을 선택해주세요'); return; }
    let successCount = 0;
    for (const employeeId of selectedEmployeeIds) {
      try { await calcMutation.mutateAsync(employeeId); successCount++; } catch (err) {}
    }
    if (successCount > 0) toast.success(`${successCount}명의 정산이 생성되었습니다`);
    setCreateModalOpen(false);
    setSelectedEmployeeIds([]);
  };

  const handleToggleEmployee = employeeId => {
    setSelectedEmployeeIds(prev =>
      prev.includes(employeeId) ? prev.filter(id => id !== employeeId) : [...prev, employeeId]);
  };

  const handleSelectAll = () => {
    const availableIds = [];
    employeeGroups.forEach(group => {
      (group.employees || []).forEach(emp => {
        if (emp.salaryType && !settledEmployeeIds.has(emp.employeeId)) availableIds.push(emp.employeeId);
      });
    });
    setSelectedEmployeeIds(selectedEmployeeIds.length === availableIds.length ? [] : availableIds);
  };

  return (
    <AnimationPage>
      <Backdrop open={calcMutation.isPending} sx={{ color: '#fff', zIndex: 9999, flexDirection: 'column', gap: '16px' }}>
        <CircularProgress color="inherit" />
        <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>정산 처리 중...</Typography>
      </Backdrop>
      <PageWrapper>
        {/* 월 네비게이션 */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div onClick={() => setCurrentDate(prev => prev.subtract(1, 'month'))}
              style={{ color: '#BFBFBF', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <ArrowBackIosNewIcon style={{ fontSize: '18px' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', minWidth: '220px' }}>
              {/* 년도 드롭다운 */}
              <div ref={yearRef} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => { setYearDropOpen(prev => !prev); setMonthDropOpen(false); }}>
                <span style={{ fontSize: '28px', fontWeight: 700, color: '#262626' }}>{currentDate.format('YYYY')}년</span>
                <span style={{ fontSize: '16px', color: '#BFBFBF', marginLeft: '2px', transition: 'transform 0.2s', transform: yearDropOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
                {yearDropOpen && (
                  <div style={{
                    position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
                    marginTop: '8px', background: '#fff', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                    border: '1px solid #E8E8E8', padding: '8px 0', zIndex: 100, maxHeight: '240px', overflowY: 'auto',
                    minWidth: '120px',
                  }}>
                    {Array.from({ length: 10 }, (_, i) => dayjs().year() - 5 + i).map(y => (
                      <div key={y} onClick={e => { e.stopPropagation(); setCurrentDate(prev => prev.year(y)); setYearDropOpen(false); }}
                        style={{
                          padding: '10px 20px', fontSize: '15px', fontWeight: currentDate.year() === y ? 700 : 400,
                          color: currentDate.year() === y ? '#4AB3BC' : '#404040', cursor: 'pointer',
                          background: currentDate.year() === y ? '#F0FAF9' : 'transparent',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => { if (currentDate.year() !== y) e.currentTarget.style.background = '#F5F5F5'; }}
                        onMouseLeave={e => { if (currentDate.year() !== y) e.currentTarget.style.background = 'transparent'; }}>
                        {y}년
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* 월 드롭다운 */}
              <div ref={monthRef} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => { setMonthDropOpen(prev => !prev); setYearDropOpen(false); }}>
                <span style={{ fontSize: '28px', fontWeight: 700, color: '#262626' }}>{currentDate.format('M')}월</span>
                <span style={{ fontSize: '16px', color: '#BFBFBF', marginLeft: '2px', transition: 'transform 0.2s', transform: monthDropOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
                {monthDropOpen && (
                  <div style={{
                    position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
                    marginTop: '8px', background: '#fff', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                    border: '1px solid #E8E8E8', padding: '8px 0', zIndex: 100,
                    minWidth: '100px',
                  }}>
                    {Array.from({ length: 12 }, (_, i) => i).map(m => (
                      <div key={m} onClick={e => { e.stopPropagation(); setCurrentDate(prev => prev.month(m)); setMonthDropOpen(false); }}
                        style={{
                          padding: '10px 20px', fontSize: '15px', fontWeight: currentDate.month() === m ? 700 : 400,
                          color: currentDate.month() === m ? '#4AB3BC' : '#404040', cursor: 'pointer',
                          background: currentDate.month() === m ? '#F0FAF9' : 'transparent',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => { if (currentDate.month() !== m) e.currentTarget.style.background = '#F5F5F5'; }}
                        onMouseLeave={e => { if (currentDate.month() !== m) e.currentTarget.style.background = 'transparent'; }}>
                        {m + 1}월
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div onClick={() => setCurrentDate(prev => prev.add(1, 'month'))}
              style={{ color: '#BFBFBF', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <ArrowForwardIosIcon style={{ fontSize: '18px' }} />
            </div>
            <div onClick={() => setCurrentDate(dayjs())}
              style={{
                padding: '8px 20px', borderRadius: '8px', background: '#333', color: '#fff',
                fontSize: '14px', fontWeight: 600, cursor: 'pointer',
              }}>
              이번달
            </div>
          </div>
        </div>

        {/* 요약 카드 */}
        <SummaryRow>
          <SummaryCard topColor={stats.confirmed > 0 ? '#52C41A' : '#D9D9D9'}>
            <SummaryLabel>급여 확정 인원</SummaryLabel>
            <SummaryValue>{stats.confirmed}명</SummaryValue>
            {stats.confirmed > 0 && <SummaryBadge positive>{stats.confirmPct}% 완료</SummaryBadge>}
          </SummaryCard>
          <SummaryCard topColor={stats.draft > 0 ? '#CF1322' : '#D9D9D9'}>
            <SummaryLabel>급여 미확정 인원</SummaryLabel>
            <SummaryValue sx={{ color: stats.draft > 0 ? '#CF1322' : '#D9D9D9' }}>{stats.draft}명</SummaryValue>
            {stats.draft > 0 && <SummaryBadge positive={false}>{stats.draftPct}% 남음</SummaryBadge>}
          </SummaryCard>
          <SummaryCard topColor={stats.totalLesson > 0 ? (Number(stats.lessonPct) >= 0 ? '#52C41A' : '#CF1322') : '#D9D9D9'}>
            <SummaryLabel>총 정산 수업</SummaryLabel>
            <SummaryValue>{formatterCurrency(stats.totalLesson)}회</SummaryValue>
            {stats.totalLesson > 0 && <PctBadge value={stats.lessonPct} label="전월 대비" />}
          </SummaryCard>
          <SummaryCard topColor={stats.totalPay > 0 ? (Number(stats.payPct) >= 0 ? '#52C41A' : '#CF1322') : '#D9D9D9'}>
            <SummaryLabel>급여액 합계</SummaryLabel>
            <SummaryValue>{formatterCurrency(stats.totalPay)}원</SummaryValue>
            {stats.totalPay > 0 && <PctBadge value={stats.payPct} label="전월 대비" />}
          </SummaryCard>
          <SummaryCard topColor={stats.totalDeduction > 0 ? (Number(stats.deductionPct) <= 0 ? '#52C41A' : '#CF1322') : '#D9D9D9'}>
            <SummaryLabel>공제액 합계</SummaryLabel>
            <SummaryValue>{formatterCurrency(stats.totalDeduction)}원</SummaryValue>
            {stats.totalDeduction > 0 && <PctBadge value={stats.deductionPct} label="전월 대비" invertColor />}
          </SummaryCard>
          <SummaryCard topColor={stats.totalNet > 0 ? (Number(stats.netPct) >= 0 ? '#52C41A' : '#CF1322') : '#D9D9D9'}>
            <SummaryLabel>실지급액 합계</SummaryLabel>
            <SummaryValue sx={{ color: stats.totalNet > 0 ? '#4AB3BC' : '#D9D9D9' }}>{formatterCurrency(stats.totalNet)}원</SummaryValue>
            {stats.totalNet > 0 && <PctBadge value={stats.netPct} label="전월 대비" />}
          </SummaryCard>
        </SummaryRow>

        {/* 차트 영역 */}
        <ChartRow>
          <ChartCard sx={{ flex: 1.5 }}>
            <ChartTitle>
              <BarChartIcon /> 월별 급여 추이({year}년)
              <Box sx={{ display: 'flex', gap: '12px', ml: 'auto', fontSize: '12px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}><ChartDot color="#4AB3BC" />실지급액</Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}><ChartDot color="#FFB547" />공제액</Box>
              </Box>
            </ChartTitle>
            <BarChart data={barChartData} height={180} />
          </ChartCard>
          <ChartCard sx={{ flex: 1 }}>
            <ChartTitle>
              <DonutChartIcon /> {month}월 급여 유형별 분포
              <Box sx={{ display: 'flex', gap: '12px', ml: 'auto', fontSize: '12px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}><ChartDot color="#4AB3BC" />횟수제 강사</Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}><ChartDot color="#FFB547" />시간제 직원</Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}><ChartDot color="#55C982" />월급제 직원</Box>
              </Box>
            </ChartTitle>
            {typeDistribution.length > 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                <DonutChart
                  data={typeDistribution.map(t => ({ label: t.label, value: t.count }))}
                  size={140}
                />
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {typeDistribution.map((t, i) => {
                    const totalCount = typeDistribution.reduce((s, v) => s + v.count, 0) || 1;
                    const pct = (t.count / totalCount * 100).toFixed(1);
                    return (
                      <Box key={t.key}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '4px' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600 }}>
                            <ChartDot color={DONUT_COLORS[i]} />{t.label} {pct}%
                          </Box>
                        </Box>
                        <Typography sx={{ fontSize: '12px', color: '#8c8c8c', mb: '6px' }}>
                          {formatterCurrency(t.total)}원 · {t.count}명
                        </Typography>
                        <Box sx={{ width: '100%', height: '6px', borderRadius: '3px', background: '#F0F0F0', overflow: 'hidden' }}>
                          <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: '3px', background: DONUT_COLORS[i], transition: 'width 0.3s ease' }} />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                <DonutChart data={[]} size={140} />
                <Box sx={{ flex: 1, textAlign: 'center', color: '#999', fontSize: '13px' }}>{year}년 {month}월의 정산 데이터가 없습니다</Box>
              </Box>
            )}
          </ChartCard>
        </ChartRow>

        {/* 하단: TOP5 + 수업 현황 */}
        <BottomRow>
          <BottomCard>
            <ChartTitle>
              <TrophyIcon /> 실지급액 TOP 5
              <Typography sx={{ ml: 'auto', fontSize: '12px', color: '#8c8c8c' }}>{year}년 {month}월</Typography>
            </ChartTitle>
            <MiniTable>
              <MiniTableHead>
                <MiniTableHeadCell flex={0.5}>순위</MiniTableHeadCell>
                <MiniTableHeadCell flex={1}>임직원</MiniTableHeadCell>
                <MiniTableHeadCell flex={1}>급여유형</MiniTableHeadCell>
                <MiniTableHeadCell flex={1.2}>실지급액</MiniTableHeadCell>
              </MiniTableHead>
              {top5.map((s, i) => (
                <MiniTableRow key={s.id}>
                  <MiniTableCell flex={0.5}>{i < 3 ? MEDAL_ICONS[i] : i + 1}</MiniTableCell>
                  <MiniTableCell flex={1}>{s.employeeName}</MiniTableCell>
                  <MiniTableCell flex={1}>
                    <SalaryTypeBadge>{SALARY_TYPE_LABELS[s.salaryType]}</SalaryTypeBadge>
                  </MiniTableCell>
                  <MiniTableCell flex={1.2} highlight>{formatterCurrency(s.netPayAmount)}원</MiniTableCell>
                </MiniTableRow>
              ))}
              {top5.length === 0 && <EmptyMessage>데이터 없음</EmptyMessage>}
            </MiniTable>
          </BottomCard>
          <BottomCard>
            <ChartTitle>
              <LessonChartIcon /> 횟수제 강사 수업 현황
              <Box sx={{ display: 'flex', gap: '12px', ml: 'auto', fontSize: '12px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}><ChartDot color={LESSON_COLORS.group} />그룹레슨</Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}><ChartDot color={LESSON_COLORS.private} />개인레슨</Box>
              </Box>
            </ChartTitle>
            <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              {countInstructors.map(s => {
                const privateCount = s.privateLessonCount || 0;
                const groupCount = s.groupLessonCount || 0;
                const total = privateCount + groupCount;
                const maxH = 100;
                const totalH = maxLessonCount > 0 ? Math.max((total / maxLessonCount) * maxH, 4) : 4;
                const privateH = total > 0 ? (privateCount / total) * totalH : 0;
                const groupH = total > 0 ? (groupCount / total) * totalH : 0;
                return (
                  <Box key={s.id} sx={{ width: '60px', textAlign: 'center', position: 'relative', cursor: 'pointer',
                    '&:hover .lesson-tooltip': { opacity: 1, visibility: 'visible' } }}>
                    <Box className="lesson-tooltip" sx={{
                      position: 'absolute', bottom: `${totalH + 50}px`, left: '50%', transform: 'translateX(-50%)',
                      background: '#333', color: '#fff', borderRadius: '6px', padding: '6px 10px', fontSize: '11px',
                      whiteSpace: 'nowrap', opacity: 0, visibility: 'hidden', transition: 'all 0.15s', zIndex: 10,
                      '&::after': { content: '""', position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
                        border: '4px solid transparent', borderTopColor: '#333' },
                    }}>
                      <Box sx={{ fontWeight: 700, mb: '2px' }}>{s.employeeName}</Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Box sx={{ width: 6, height: 6, borderRadius: '1px', background: LESSON_COLORS.private, flexShrink: 0 }} />개인레슨 : {privateCount}회</Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Box sx={{ width: 6, height: 6, borderRadius: '1px', background: LESSON_COLORS.group, flexShrink: 0 }} />그룹레슨 : {groupCount}회</Box>
                    </Box>
                    <Box sx={{ height: `${maxH}px`, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', mb: '4px' }}>
                      <Box sx={{ width: '32px', borderRadius: '4px 4px 0 0', background: LESSON_COLORS.private, height: `${privateH}px` }} />
                      <Box sx={{ width: '32px', borderRadius: groupH > 0 && privateH === 0 ? '4px 4px 0 0' : '0', background: LESSON_COLORS.group, height: `${groupH}px` }} />
                    </Box>
                    <Typography sx={{ fontSize: '11px', color: '#404040', fontWeight: 600 }}>{total}회</Typography>
                    <Typography sx={{ fontSize: '10px', color: '#8c8c8c', mt: '2px' }}>{s.employeeName}</Typography>
                  </Box>
                );
              })}
              {countInstructors.length === 0 && <EmptyMessage>횟수제 강사 없음</EmptyMessage>}
            </Box>
          </BottomCard>
        </BottomRow>

        {/* 테이블 */}
        <TableCard>
          {/* 필터 탭 */}
          <FilterRow>
            <FilterLeft>
              {TYPE_FILTERS.map(f => {
                const cnt = f.key === 'ALL' ? settlements.length : settlements.filter(s => s.salaryType === f.key).length;
                return (
                  <FilterTab key={f.key} active={typeFilter === f.key} onClick={() => { setTypeFilter(f.key); setPage(1); }}>
                    {f.label} <span style={{ fontWeight: 700, marginLeft: '4px' }}>{cnt}</span>
                  </FilterTab>
                );
              })}
              {/* 미정산만 보기 */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', ml: '16px', cursor: 'pointer' }}
                onClick={() => { setOnlyDraft(prev => !prev); setPage(1); }}>
                <Switch checked={onlyDraft} size="small"
                  sx={{
                    width: 36, height: 20, padding: 0,
                    '& .MuiSwitch-switchBase': { padding: '2px',
                      '&.Mui-checked': { transform: 'translateX(16px)', color: '#fff',
                        '& + .MuiSwitch-track': { backgroundColor: '#4AB3BC', opacity: 1 } } },
                    '& .MuiSwitch-thumb': { width: 16, height: 16 },
                    '& .MuiSwitch-track': { borderRadius: 10, backgroundColor: '#D9D9D9', opacity: 1 },
                  }} />
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#404040' }}>미정산만 보기</Typography>
              </Box>
              <CreateButton sx={{ ml: '16px' }} onClick={() => { setSelectedEmployeeIds([]); setCreateModalOpen(true); }}>
                <AddIcon sx={{ fontSize: '16px' }} />정산 생성
              </CreateButton>
            </FilterLeft>
            <FilterRight>
              <GuideButton onClick={() => setGuideOpen(true)}>📖 급여 정산 가이드</GuideButton>
              <FilterSelect value={positionFilter} onChange={e => { setPositionFilter(e.target.value); setPage(1); }}>
                <option value="ALL">전체 직급</option>
                {positions.map(p => <option key={p} value={p}>{p}</option>)}
              </FilterSelect>
              <CommonExcelButton onClick={handleExcelDownload}>
                전체 엑셀 다운로드
              </CommonExcelButton>
              <FilterSelect value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
                <option value={10}>10개씩</option>
                <option value={20}>20개씩</option>
                <option value={50}>50개씩</option>
              </FilterSelect>
            </FilterRight>
          </FilterRow>

          <Table>
            <TableHead>
              <TableHeadCell flex={0.55}>정산상태</TableHeadCell>
              <TableHeadCell flex={0.5}>임직원</TableHeadCell>
              <TableHeadCell flex={0.6}>급여유형</TableHeadCell>
              <TableHeadCell flex={0.45}>수업일수</TableHeadCell>
              <TableHeadCell flex={0.5}>산정기준</TableHeadCell>
              <TableHeadCell flex={0.75}>정산 수업/근로시간</TableHeadCell>
              <TableHeadCell flex={0.55}>야간근로</TableHeadCell>
              <TableHeadCell flex={0.55}>휴일근로</TableHeadCell>
              <TableHeadCell flex={0.7}>급여총액</TableHeadCell>
              <TableHeadCell flex={0.55}>공제액</TableHeadCell>
              <TableHeadCell flex={0.7}>실지급액</TableHeadCell>
              <TableHeadCell flex={0.55}>생성일</TableHeadCell>
              <TableHeadCell flex={0.55}>재계산일</TableHeadCell>
              <TableHeadCell flex={0.7}>정산/급여명세서</TableHeadCell>
              <TableHeadCell flex={0.35}>상세보기</TableHeadCell>
            </TableHead>

            {pagedSettlements.map(s => {
              const isExpanded = expandedId === s.id;
              const salaryBasis = s.salaryType === 'COUNT' ? '수업 기준' : s.salaryType === 'HOURLY' ? '시간기준' : '월급기준';
              const lessonOrHours = s.salaryType === 'HOURLY'
                ? (s.totalWorkMinutes ? `${(s.totalWorkMinutes / 60).toFixed(1).replace(/\.0$/, '')}시간` : '-')
                : (s.totalLessonCount > 0 ? `${s.totalLessonCount}회` : '-');
              return (
                <Box key={s.id}>
                  <TableRow expanded={isExpanded} onClick={() => setExpandedId(isExpanded ? null : s.id)}>
                    <TableCell flex={0.55}>
                      <StatusBadge status={s.status}>{STATUS_LABELS[s.status]}</StatusBadge>
                    </TableCell>
                    <TableCell flex={0.5}>{s.employeeName}</TableCell>
                    <TableCell flex={0.6}><SalaryTypeBadge>{SALARY_TYPE_LABELS[s.salaryType]}</SalaryTypeBadge></TableCell>
                    <TableCell flex={0.45}>{s.workDays || 0}일</TableCell>
                    <TableCell flex={0.5} sx={{ fontSize: '12px', color: '#8c8c8c' }}>{salaryBasis}</TableCell>
                    <TableCell flex={0.75}>{lessonOrHours}</TableCell>
                    <TableCell flex={0.55}>{s.nightWorkAmount > 0 ? formatterCurrency(s.nightWorkAmount) : '-'}</TableCell>
                    <TableCell flex={0.55}>{s.holidayWorkAmount > 0 ? formatterCurrency(s.holidayWorkAmount) : '-'}</TableCell>
                    <TableCell flex={0.7}>{formatterCurrency(s.totalPayAmount)}</TableCell>
                    <TableCell flex={0.55}>{formatterCurrency(s.deductionAmount)}</TableCell>
                    <TableCell flex={0.7} highlight>{formatterCurrency(s.netPayAmount)}</TableCell>
                    <TableCell flex={0.55} sx={{ fontSize: '12px', color: '#8c8c8c' }}>{s.createdDate || '-'}</TableCell>
                    <TableCell flex={0.55} sx={{ fontSize: '12px', color: '#8c8c8c' }}>{s.recalculatedDate || '-'}</TableCell>
                    <TableCell flex={0.7}>
                      <Box onClick={e => {
                        e.stopPropagation();
                        const step = s.status === 'CONFIRMED' ? '&step=3' : '';
                        navigate(`/salarySettlement/${s.id}?year=${year}&month=${month}${step}`);
                      }} sx={{
                        padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                        ...(s.status === 'CONFIRMED'
                          ? { border: '1px solid #D9D9D9', color: '#595959', '&:hover': { background: '#F5F5F5' } }
                          : { background: '#333', color: '#fff', '&:hover': { background: '#555' } }),
                      }}>
                        {s.status === 'CONFIRMED' ? '급여명세서' : '정산하기'}
                      </Box>
                    </TableCell>
                    <TableCell flex={0.35}>
                      <Box sx={{ width: '24px', height: '24px', borderRadius: '4px', border: '1px solid #D9D9D9',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: '#595959',
                        cursor: 'pointer', '&:hover': { background: '#F5F5F5' } }}>
                        {isExpanded ? '−' : '+'}
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* 확장 상세 */}
                  {isExpanded && (
                    <Box sx={{ display: 'flex', gap: '32px', borderBottom: '2px solid #E8E8E8', background: '#F0FAF9', padding: '16px 24px' }}>
                     <Box sx={{ display: 'flex', gap: '32px', background: '#fff', borderRadius: '10px', padding: '20px 24px', flex: 1, border: '1px solid #E8E8E8' }}>
                      {/* 근로정보 */}
                      <Box sx={{ flex: 1, borderRight: '1px solid #E8E8E8', pr: '24px' }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#595959', mb: '16px', pb: '8px', borderBottom: '1px solid #E8E8E8' }}>근로정보</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13px', color: '#404040' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>수업일수</span>
                            <span style={{ fontWeight: 600 }}>{s.workDays || 0}일</span>
                          </Box>
                          {s.salaryType === 'COUNT' && <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>정산 수업</span>
                              <span style={{ fontWeight: 600 }}>{s.totalLessonCount || 0}회</span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>그룹레슨</span>
                              <span style={{ fontWeight: 600 }}>{s.groupLessonCount || 0}회</span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>개인레슨</span>
                              <span style={{ fontWeight: 600 }}>{s.privateLessonCount || 0}회</span>
                            </Box>
                          </>}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>생성일</span>
                            <span style={{ fontWeight: 600 }}>{s.createdDate || '-'}</span>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>재계산일</span>
                            <span style={{ fontWeight: 600 }}>{s.recalculatedDate || '-'}</span>
                          </Box>
                        </Box>
                      </Box>
                      {/* 소득 */}
                      <Box sx={{ flex: 1.2, borderRight: '1px solid #E8E8E8', pr: '24px' }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#595959', mb: '16px', pb: '8px', borderBottom: '1px solid #E8E8E8' }}>소득</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13px', color: '#404040' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>기본급</span>
                            <span style={{ fontWeight: 600 }}>{formatterCurrency(s.baseAmount)}원</span>
                          </Box>
                          {s.privateLessonAmount > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>수업 인센티브</span>
                              <span style={{ fontWeight: 600, color: '#4AB3BC' }}>+{formatterCurrency(s.privateLessonAmount + (s.groupLessonAmount || 0))}원</span>
                            </Box>
                          )}
                          {s.overflowAmount > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>초과수당</span>
                              <span style={{ fontWeight: 600, color: '#4AB3BC' }}>+{formatterCurrency(s.overflowAmount)}원</span>
                            </Box>
                          )}
                          {s.bonusAmount > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>성과급</span>
                              <span style={{ fontWeight: 600, color: '#4AB3BC' }}>+{formatterCurrency(s.bonusAmount)}원</span>
                            </Box>
                          )}
                        </Box>
                      </Box>
                      {/* 비과세소득 */}
                      <Box sx={{ flex: 1, borderRight: '1px solid #E8E8E8', pr: '24px' }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#595959', mb: '16px', pb: '8px', borderBottom: '1px solid #E8E8E8' }}>비과세소득</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13px', color: '#404040' }}>
                          {s.taxExemptAmount > 0 ? (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>비과세 합계</span>
                              <span style={{ fontWeight: 600 }}>{formatterCurrency(s.taxExemptAmount)}원</span>
                            </Box>
                          ) : <Box sx={{ color: '#999' }}>-</Box>}
                        </Box>
                      </Box>
                      {/* 공제 */}
                      <Box sx={{ flex: 1.2 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#595959', mb: '16px', pb: '8px', borderBottom: '1px solid #E8E8E8' }}>공제</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13px', color: '#404040' }}>
                          {(s.deductionDetails || []).map((d, i) => (
                            <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>{d.itemName}</span>
                              <span style={{ fontWeight: 600, color: '#CF1322' }}>-{formatterCurrency(d.amount)}원</span>
                            </Box>
                          ))}
                          {(!s.deductionDetails || s.deductionDetails.length === 0) && <Box sx={{ color: '#999' }}>-</Box>}
                        </Box>
                      </Box>
                     </Box>
                    </Box>
                  )}
                </Box>
              );
            })}

            {filteredSettlements.length === 0 && (
              <EmptyMessage>{year}년 {month}월의 정산 데이터가 없습니다.</EmptyMessage>
            )}
          </Table>

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px', padding: '16px 0' }}>
              <Box onClick={() => setPage(p => Math.max(1, p - 1))} sx={{
                width: '32px', height: '32px', borderRadius: '6px', border: '1px solid #E8E8E8',
                display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                color: '#8c8c8c', '&:hover': { background: '#F0F0F0' },
              }}>{'<'}</Box>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                const p = start + i;
                if (p > totalPages) return null;
                return (
                  <Box key={p} onClick={() => setPage(p)} sx={{
                    width: '32px', height: '32px', borderRadius: '6px', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 600,
                    cursor: 'pointer',
                    background: page === p ? '#4AB3BC' : 'transparent',
                    color: page === p ? '#fff' : '#595959',
                    '&:hover': { background: page === p ? '#3A9DA5' : '#F0F0F0' },
                  }}>{p}</Box>
                );
              })}
              {totalPages > 5 && <Box sx={{ fontSize: '13px', color: '#8c8c8c' }}>...</Box>}
              {totalPages > 5 && (
                <Box onClick={() => setPage(totalPages)} sx={{
                  width: '32px', height: '32px', borderRadius: '6px', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 600,
                  cursor: 'pointer', color: page === totalPages ? '#fff' : '#595959',
                  background: page === totalPages ? '#4AB3BC' : 'transparent',
                }}>{totalPages}</Box>
              )}
              <Box onClick={() => setPage(p => Math.min(totalPages, p + 1))} sx={{
                width: '32px', height: '32px', borderRadius: '6px', border: '1px solid #E8E8E8',
                display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                color: '#8c8c8c', '&:hover': { background: '#F0F0F0' },
              }}>{'>'}</Box>
            </Box>
          )}
        </TableCard>
      </PageWrapper>

      {/* 정산 생성 모달 */}
      <Modal open={createModalOpen} onClose={() => setCreateModalOpen(false)} disableScrollLock>
        <ModalContainer>
          <ModalHeader>
            <ModalTitle>정산 생성 — {currentDate.format('YYYY년 MM월')}</ModalTitle>
            <Close sx={{ cursor: 'pointer' }} onClick={() => setCreateModalOpen(false)} />
          </ModalHeader>
          <ModalBody>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '8px' }}>
              <Typography sx={{ fontSize: '13px', color: '#8c8c8c' }}>급여 설정이 완료된 직원만 표시됩니다</Typography>
              <Box sx={{ fontSize: '13px', color: '#4AB3BC', cursor: 'pointer', fontWeight: 600 }} onClick={handleSelectAll}>전체 선택</Box>
            </Box>
            {employeeGroups.map(group => {
              const avail = (group.employees || []).filter(e => e.salaryType && !settledEmployeeIds.has(e.employeeId));
              if (avail.length === 0) return null;
              return (
                <Box key={group.positionName}>
                  <PositionGroupTitle>{group.positionName} ({avail.length}명)</PositionGroupTitle>
                  {avail.map(emp => (
                    <EmployeeRow key={emp.employeeId} onClick={() => handleToggleEmployee(emp.employeeId)}>
                      <TealCheckbox checked={selectedEmployeeIds.includes(emp.employeeId)} disableRipple />
                      <EmployeeAvatar src={emp.profileImage || undefined} />
                      <EmployeeInfo>
                        <EmployeeName>{emp.name}</EmployeeName>
                        <EmployeeSub>{emp.phone} · {SALARY_TYPE_LABELS[emp.salaryType] || ''}</EmployeeSub>
                      </EmployeeInfo>
                    </EmployeeRow>
                  ))}
                </Box>
              );
            })}
            {employeeGroups.every(g => (g.employees || []).every(e => !e.salaryType || settledEmployeeIds.has(e.employeeId))) && (
              <EmptyMessage>정산 가능한 직원이 없습니다.</EmptyMessage>
            )}
          </ModalBody>
          <ModalFooter>
            <CommonModalButton variant="cancel" onClick={() => setCreateModalOpen(false)}>취소</CommonModalButton>
            <CommonModalButton variant="submit" onClick={() => setCreateConfirmModalOpen(true)}
              disabled={calcMutation.isPending || selectedEmployeeIds.length === 0}>
              {selectedEmployeeIds.length > 0 ? `${selectedEmployeeIds.length}명 정산 생성` : '직원을 선택해주세요'}
            </CommonModalButton>
          </ModalFooter>
        </ModalContainer>
      </Modal>

      <Modal open={createConfirmModalOpen} onClose={() => setCreateConfirmModalOpen(false)} disableScrollLock>
        <ConfirmModalContainer>
          <ConfirmModalText>
            <span style={{ color: '#4AB3BC', fontWeight: 700, fontSize: '26px' }}>{selectedEmployeeIds.length}명</span>의 정산을 생성하시겠습니까?
          </ConfirmModalText>
          <ConfirmModalButtons>
            <CommonModalButton variant="cancel" onClick={() => setCreateConfirmModalOpen(false)}>닫기</CommonModalButton>
            <CommonModalButton variant="submit" onClick={() => { setCreateConfirmModalOpen(false); handleCreateSettlements(); }} disabled={calcMutation.isPending}>생성</CommonModalButton>
          </ConfirmModalButtons>
        </ConfirmModalContainer>
      </Modal>

      {/* 정산 가이드 */}
      <Modal open={guideOpen} onClose={() => setGuideOpen(false)} disableScrollLock>
        <GuideModalContainer>
          <GuideModalHeader>
            <GuideModalTitle>급여 정산 가이드</GuideModalTitle>
            <Close onClick={() => setGuideOpen(false)} sx={{ color: '#fff', cursor: 'pointer' }} />
          </GuideModalHeader>
          <GuideModalBody>
            <GuideSection>
              <GuideSectionLabel>📊 급여 정산이란?</GuideSectionLabel>
              <GuideText>급여 설정을 기반으로 직원의 수업 이력을 조회하고, 급여를 자동 계산하여 명세서를 생성하는 과정입니다.</GuideText>
            </GuideSection>
            <GuideDivider />
            <GuideSection>
              <GuideSectionLabel>📋 정산 플로우</GuideSectionLabel>
              <GuideStepBox>
                <GuideStepNumber>1</GuideStepNumber>
                <GuideStepText><strong>정산 생성</strong> 버튼을 클릭하여 정산할 직원을 선택하고 정산을 생성합니다. 급여 설정이 완료된 직원만 선택 가능합니다.</GuideStepText>
              </GuideStepBox>
              <GuideStepBox>
                <GuideStepNumber>2</GuideStepNumber>
                <GuideStepText><strong>수업 이력 확인</strong> (Step 1) — 개인레슨/그룹레슨 수업 이력과 수당이 정확한지 확인합니다.</GuideStepText>
              </GuideStepBox>
              <GuideStepBox>
                <GuideStepNumber>3</GuideStepNumber>
                <GuideStepText><strong>급여 항목 확인</strong> (Step 2) — 기본급, 수업수당, 초과수당, 성과급, 비과세, 공제 항목을 확인합니다. 필요 시 금액 조정이 가능합니다.</GuideStepText>
              </GuideStepBox>
              <GuideStepBox>
                <GuideStepNumber>4</GuideStepNumber>
                <GuideStepText><strong>명세서 확인</strong> (Step 3) — 급여 명세서를 미리보기하고, 인쇄 또는 PDF로 저장할 수 있습니다.</GuideStepText>
              </GuideStepBox>
              <GuideStepBox>
                <GuideStepNumber>5</GuideStepNumber>
                <GuideStepText><strong>정산 완료</strong> 버튼을 눌러 정산을 확정합니다. 확정 후에는 수정이 불가능하며 급여 명세서가 확정됩니다.</GuideStepText>
              </GuideStepBox>
            </GuideSection>
            <GuideDivider />
            <GuideSection>
              <GuideSectionLabel>💡 참고 사항</GuideSectionLabel>
              <GuideText>
                • 정산 생성 전 <strong>임직원 관리</strong> → <strong>급여 설정</strong>이 먼저 완료되어야 합니다.<br />
                • 정산 완료 전까지는 <strong>재계산</strong>으로 수업 이력을 다시 불러올 수 있습니다.<br />
                • <strong>금액 조정</strong>으로 특수한 경우 수동으로 금액을 추가/차감할 수 있습니다.<br />
                • <strong>수업 사인 정산</strong> — 강사앱에서 사인을 완료한 수업만 정산에 포함하는 기능입니다.<br />
                  설정 경로: <strong>센터 관리</strong> → <strong>센터 목록</strong> → <strong>센터 상세</strong> → <strong>센터설정</strong> → <strong>급여 설정</strong> 탭<br />
                • 엑셀 다운로드로 전체 정산 내역을 내보낼 수 있습니다.
              </GuideText>
            </GuideSection>
          </GuideModalBody>
        </GuideModalContainer>
      </Modal>
    </AnimationPage>
  );
};

export default SalarySettlementList;
